import { KeyboardEventHandler, MouseEventHandler } from 'react';
import { ClassNameProp, VoidCallback } from '../../components';

export interface AccessibleElementProps extends Required<ClassNameProp> {
  /** this allows mouse activation. */
  onClick: VoidCallback | MouseEventHandler | undefined;
  /** this allows keyboard activation. */
  onKeyDown: KeyboardEventHandler | undefined;
  /** this makes the element focusable. */
  tabIndex: 0 | undefined;
}

interface AccessibleElementParams extends ClassNameProp {
  onClick?: VoidCallback | MouseEventHandler;
}

/**
 * returns props to make an arbitrary element accessible to keyboards and screen readers.
 *
 * className will be augmented with `ehs-clickable` if a click handler is passed.
 *
 * If possible, please use `AccessibleClickableContainer` instead, which is usually the better choice!
 * This hook should only be used in edge cases, such as table rows or items containing buttons.
 *
 * @author Bennett Piater
 * @author agd
 */
export function useAccessibleElementProps({
  onClick,
  className = ''
}: AccessibleElementParams): AccessibleElementProps {
  const onKeyDown =
    onClick !== undefined
      ? (event: Parameters<KeyboardEventHandler>[0]) => {
          // behave like a <button>
          if (
            // Event.currentTarget always refers to the element to which the event handler has been attached,
            // as opposed to Event.target, which identifies the element on which the event occurred and which may be its descendant
            event.currentTarget === event.target && // only if the source of the event is the element that the handler is placed on
            ['Enter', ' '].includes(event.key)
          ) {
            event.preventDefault();
            (event.currentTarget as HTMLElement).click();
          }
        }
      : undefined;

  if (onClick !== undefined) {
    className += ' ehs-clickable';
  }

  return {
    onClick,
    onKeyDown,
    className,
    tabIndex: onClick !== undefined ? 0 : undefined
  };
}
